import * as AlertDialog from '@radix-ui/react-alert-dialog'
import clsx from 'clsx'
import type { ReactNode } from 'react'

import { Button } from '@/shared/ui/Button'

/**
 * ConfirmDialog — diálogo de confirmação do DS.
 *
 * Construído sobre `@radix-ui/react-alert-dialog` (mesmo primitivo dos
 * componentes shadcn), porém estilizado 100% com tokens do DS (não depende dos
 * tokens shadcn `bg-background`/`--color-*`, que não estão definidos neste
 * stack Tailwind v4). Renderiza em `z-dialog-backdrop`/`z-dialog`, acima do
 * `MobileSheet` (`z-sheet`), com overlay escurecido. Escala em
 * `styles/tailwind.css`.
 *
 * Botões usam o `Button` do DS. Confirmar assume `primary` (default) ou
 * `danger` (`variant="destructive"`, ações destrutivas como reembolso).
 */
export type ConfirmDialogProps = {
  /** Controla a visibilidade. Default `true` (o componente costuma ser montado já aberto). */
  open?: boolean
  title: ReactNode
  description?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** `default` = confirmar primary · `destructive` = confirmar danger. */
  variant?: 'default' | 'destructive'
  /** Spinner + desabilita o confirmar (ex.: gate de leitura). */
  confirmLoading?: boolean
  confirmDisabled?: boolean
  onConfirm: () => void
  onCancel: () => void
  className?: string
}

export const ConfirmDialog = ({
  open = true,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'default',
  confirmLoading = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
  className
}: ConfirmDialogProps) => (
  <AlertDialog.Root
    open={open}
    onOpenChange={next => {
      // Fechar sem clicar em um botão (ex.: Esc) equivale a cancelar.
      if (!next) onCancel()
    }}
  >
    <AlertDialog.Portal>
      <AlertDialog.Overlay className="fixed inset-0 z-dialog-backdrop bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <AlertDialog.Content
        className={clsx(
          'fixed left-1/2 top-1/2 z-dialog -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100%-2rem)] max-w-[400px] rounded-2xl bg-white p-6 shadow-xl outline-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className
        )}
      >
        <AlertDialog.Title className="block text-center text-2xl font-bold text-gray-900">
          {title}
        </AlertDialog.Title>
        {description ? (
          <AlertDialog.Description className="mt-3 block text-center text-sm text-gray-700">
            {description}
          </AlertDialog.Description>
        ) : null}
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === 'destructive' ? 'danger' : 'primary'}
            showTrailingIcon={false}
            loading={confirmLoading}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </AlertDialog.Content>
    </AlertDialog.Portal>
  </AlertDialog.Root>
)

ConfirmDialog.displayName = 'ConfirmDialog'
